import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { EnumsResponse } from '../api/adminServices'
import type { Language } from './settingsStore'
import { buildEnumMaps, lookupEnumLabel, resolveEnumItemLabel } from '../utils/enumLabelUtils'

interface EnumState {
  enums: EnumsResponse | null
  enumMap: Record<string, string>
  /** Server user locale the cached labels belong to (from JWT / user profile). */
  loadedLocale: Language | null
  error: string | null
  setEnums: (enums: EnumsResponse, locale: Language) => void
  setError: (error: string | null) => void
  getLocalizedLabel: (enumName: string, code: string) => string
  getEnumOptions: (enumName: string) => { code: string; label: string }[]
  clearEnums: () => void
  getAvailableEnumNames: () => string[]
}

export const useEnumStore = create<EnumState>()(
  persist(
    (set, get) => ({
      enums: null,
      enumMap: {},
      loadedLocale: null,
      error: null,

      setEnums: (enums, locale) => {
        const maps = buildEnumMaps(enums, locale)
        const enumMap = maps[locale] ?? {}
        set({ enums, enumMap, loadedLocale: locale, error: null })
      },

      setError: (error) => {
        set({ error })
      },

      getLocalizedLabel: (enumName: string, code: string): string => {
        if (!code) return '-'
        const label = lookupEnumLabel(get().enumMap, enumName, code)
        return (label ?? String(code).trim()) || '-'
      },

      getEnumOptions: (enumName: string) => {
        const state = get()
        if (!state.enums) return []

        const normalized = enumName.trim()
        const direct = (state.enums as Record<string, unknown>)[normalized]
        let items: unknown = direct
        if (!Array.isArray(items)) {
          for (const [key, value] of Object.entries(state.enums)) {
            if (key.toLowerCase() === normalized.toLowerCase()) {
              items = value
              break
            }
          }
        }
        if (!Array.isArray(items)) return []

        const seen = new Set<string>()
        const result: { code: string; label: string }[] = []
        for (const item of items) {
          if (!item || typeof item !== 'object') continue
          const obj = item as { code?: unknown; label?: unknown; labels?: unknown }
          const code = obj.code != null ? String(obj.code).trim() : ''
          if (!code || seen.has(code)) continue
          seen.add(code)
          const label =
            lookupEnumLabel(state.enumMap, normalized, code) ??
            resolveEnumItemLabel(obj as Parameters<typeof resolveEnumItemLabel>[0], state.loadedLocale ?? 'EN') ??
            code
          result.push({ code, label })
        }
        return result
      },

      clearEnums: () => {
        set({ enums: null, enumMap: {}, loadedLocale: null })
      },

      getAvailableEnumNames: () => {
        const state = get()
        return state.enums ? Object.keys(state.enums) : []
      },
    }),
    {
      name: 'enum-storage-v3',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => () => {
        try {
          localStorage.removeItem('enum-storage')
          localStorage.removeItem('enum-storage-v2')
        } catch {
          /* ignore */
        }
      },
    },
  ),
)
